import { OnDestroy, DestroyRef, Injectable, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Firestore} from '@angular/fire/firestore';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  collectionData
} from '@angular/fire/firestore';
import { Observable, BehaviorSubject, Subscription, EMPTY, from, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { Loan, LoanEvent } from '@shared/definitions/loan';
import { projectLoansForProperty } from '@shared/utils/loan-allocation';
import { User } from '@shared/definitions/user';
import { LoanCalculatorService } from '@core/services/finance/loan-calculator.service';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { LoanStore } from '@shared/services/loan.store';
import { AuthStore } from '@core/services/auth/auth.store';
import { isPermissionDeniedError } from '@core/services/auth/auth-transition-error';

@Injectable({
  providedIn: 'root'
})
export class LoansService implements OnDestroy {
  private readonly collectionName = 'loans';
  loans = new BehaviorSubject<Loan[]>([]);
  private user: User | null = null;

  private loansData: Loan[] = [];
  private subsLoans: Subscription = new Subscription();

  private readonly loanStore = inject(LoanStore);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Stable dedup key — the effect only re-runs when userUID or claimsReady
   * actually change (signals auto-dedupe primitives).
   */
  private readonly authKey = computed(() => {
    const u = this.authStore.user();
    return `${u?.userUID ?? ''}:${this.authStore.claimsReady()}`;
  });

  constructor(
    private firestore: Firestore,
    private pathService: FirestorePathService,
    private loanCalculator: LoanCalculatorService,
    private workspaceStore: WorkspaceStore
  ) {
    Loan.calculator = this.loanCalculator;

    this.initializeService();

    // Phase 4 bridge: mirror the list into LoanStore.
    this.loans.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this.loanStore.setList(list));
  }

  private initializeService(): void {
    // Gate on claimsReady as well as user — see BaseCrudService.initializeService
    // for the rationale: a fresh signup emits an authenticated user before the
    // workspace custom claim lands in the ID token, so workspace-scoped reads
    // fail with "Missing or insufficient permissions" until the signup CF
    // finishes and `refreshToken()` pulls the new claim down.
    effect(async () => {
      const _key = this.authKey();
      const user = this.authStore.user();
      const claimsReady = this.authStore.claimsReady();

      this.user = user;
      if (!user || !user.userUID) {
        this.clearLoans();
        return;
      }
      if (!claimsReady) return;

      try {
        await this.workspaceStore.waitForWorkspace(10000);
      } catch (_error) {
        console.error('❌ LoansService: Workspace timeout');
        return;
      }
      this.fetchAllLoans();
    });
  }

  ngOnDestroy(): void {
    this.subsLoans.unsubscribe();
  }

  /**
   * Get the user-scoped collection reference
   */
  private getCollection() {
    const path = this.pathService.getWorkspaceCollection(this.collectionName);
    return collection(this.firestore, path);
  }

  /**
   * Get a user-scoped document reference
   */
  private getDocument(documentId: string) {
    const path = this.pathService.getWorkspaceDocument(this.collectionName, documentId);
    return doc(this.firestore, path);
  }

  /**
   * Check if current user is authenticated
   */
  private isAuthenticated(): boolean {
    return !!this.authStore.user()?.userUID;
  }

  /**
   * Serialise loan amendments for Firestore, stripping every `undefined`
   * optional field (the frontend SDK has no `ignoreUndefinedProperties`, so a
   * single undefined inside the array would reject the whole write).
   */
  private serializeEvents(events: LoanEvent[] | undefined): Record<string, unknown>[] {
    return (events ?? []).map(e => {
      const o: Record<string, unknown> = {
        id: e.id,
        type: e.type,
        effectiveDate: e.effectiveDate
      };
      if (typeof e.amount === 'number') o['amount'] = e.amount;
      if (e.repaymentMode) o['repaymentMode'] = e.repaymentMode;
      if (e.suspensionEndDate) o['suspensionEndDate'] = e.suspensionEndDate;
      if (e.suspensionScope) o['suspensionScope'] = e.suspensionScope;
      if (e.suspensionAfter) o['suspensionAfter'] = e.suspensionAfter;
      if (typeof e.newRate === 'number') o['newRate'] = e.newRate;
      if (e.rateRecalc) o['rateRecalc'] = e.rateRecalc;
      if (typeof e.newNumberOfMonths === 'number') o['newNumberOfMonths'] = e.newNumberOfMonths;
      if (e.closureReason) o['closureReason'] = e.closureReason;
      if (e.refinancedToLoanUID) o['refinancedToLoanUID'] = e.refinancedToLoanUID;
      if (typeof e.indemnity === 'number') o['indemnity'] = e.indemnity;
      if (typeof e.fees === 'number') o['fees'] = e.fees;
      if (e.note) o['note'] = e.note;
      if (e.documentUID) o['documentUID'] = e.documentUID;
      if (e.createdAt) o['createdAt'] = e.createdAt;
      return o;
    });
  }

  /**
   * Helper method to add standard timestamps to document data
   */
  private addTimestamps<T extends Record<string, unknown>>(data: T, isUpdate = false): T & {
    createdAt?: Date;
    updatedAt: Date;
  } {
    const now = new Date();
    const result: T & { createdAt?: Date; updatedAt: Date } = { ...data, updatedAt: now };

    if (!isUpdate) {
      result.createdAt = now;
    }

    return result;
  }

  public fetchAllLoans(): void {
    if (!this.isAuthenticated()) {
      console.warn('Cannot fetch loans: No authenticated user');
      return;
    }

    // Unsubscribe from previous subscription
    this.subsLoans.unsubscribe();
    
    // Use the new user-scoped collection - no need for where clause
    const loansCollection = this.getCollection();
    
    this.subsLoans = collectionData(loansCollection, { idField: 'loanUID' }).pipe(
      map(loans => loans.map(loan => {
        const loanObj = new Loan(loan as Partial<Loan>);
        loanObj.calculateLoanTable();
        return loanObj;
      })),
      catchError(this.handleError),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(loans => {
      this.loansData = loans;
      this.loans.next(this.loansData);
    });
  }

  getLoansByProperty(propertyUID: string): Observable<Loan[]> {
    return this.loans.pipe(
      // Direct loans + pro-rata projection of any building loan allocating here.
      map(loans => projectLoansForProperty(loans, propertyUID))
    );
  }

  getLoanById(loanUID: string): Observable<Loan | null> {
    return this.loans.pipe(
      map(loans => {
        const loan = loans.find(loan => loan.loanUID === loanUID);
        if (loan) {
          const loanObj = new Loan(loan);
          loanObj.calculateLoanTable();
          return loanObj;
        }
        return null;
      })
    );
  }

  addLoan(loan: Omit<Loan, 'loanUID'>): Observable<string> {
    if (!this.isAuthenticated()) {
      console.error('[LoansService] Add failed: User not authenticated');
      return throwError(() => new Error('User not authenticated'));
    }

    const newLoan = new Loan(loan);
    newLoan.calculateLoanTable();
    // Remove ownerUID assignment since it's implicit in the user-nested path
    // newLoan.ownerUID = this.user.userUID; // No longer needed

    const loanToSave = {
      ...newLoan,
      loanProvider: newLoan.loanProvider || { name: '', lenderUID: '' },
      // Lifecycle amendments + status (undefined-stripped per event).
      loanEvents: this.serializeEvents(newLoan.loanEvents),
      status: newLoan.status,
      loanTable: newLoan.loanTable.map(entry => ({
        period: entry.period,
        date: entry.date,
        startingPrincipal: entry.startingPrincipal,
        interest: entry.interest,
        principalRepayment: entry.principalRepayment,
        endingPrincipal: entry.endingPrincipal,
        fees: entry.fees,
        totalCashout: entry.totalCashout
      }))
      // ownerUID removed - implicit in user-nested path
    };

    // Use addTimestamps helper
    const loanData = this.addTimestamps(loanToSave, false);

    // Use the user-scoped collection
    const loansCollection = this.getCollection();

    return from(addDoc(loansCollection, loanData)).pipe(
      map(docRef => {
        const loanUID = docRef.id;
        return loanUID;
      }),
      tap(loanUID => {
        newLoan.loanUID = loanUID;
        // Dedup: Firestore's snapshot listener can fire from the local cache
        // the moment addDoc is invoked — i.e. before this tap runs — and
        // populate loansData with the server-shaped doc. Pushing again here
        // would produce the loan twice in the list view until a hard refresh
        // (mirrors the property-list duplicate-after-create bug).
        if (!this.loansData.some(l => l.loanUID === loanUID)) {
          this.loansData.push(newLoan);
          this.loans.next([...this.loansData]);
        }
      }),
      catchError(error => {
        console.error('[LoansService] Add error:', error);
        return throwError(() => new Error(error.message || 'Database error'));
      })
    );
  }

  updateLoan(loanUID: string, loanData: Partial<Loan>): Observable<void> {
    // Only log errors in production mode
    // console.log('[LoansService] Starting loan update for UID:', loanUID);
    
    if (!this.isAuthenticated()) {
      console.error('[LoansService] Update failed: User not authenticated');
      return throwError(() => new Error('User not authenticated'));
    }

    const loanDocRef = this.getDocument(loanUID);
    const existingLoan = this.loansData.find(loan => loan.loanUID === loanUID);

    if (!existingLoan) {
      console.error('[LoansService] Update failed: Loan not found in local data');
      return throwError(() => new Error('Loan not found'));
    }
    
    // Remove ownerUID logic since it's implicit in the user-nested path
    // if (!loanData.ownerUID) {
    //   loanData.ownerUID = existingLoan.ownerUID || this.user.userUID;
    // }
    
    // Ensure loanUID is preserved
    if (!loanData.loanUID) {
      loanData.loanUID = loanUID;
    }

    const updatedLoan = new Loan({ ...existingLoan, ...loanData });
    updatedLoan.calculateLoanTable();

    const loanToUpdate = {
      // Firestore won't allow updating UID fields, so we don't include them in the update
      // But we do need to maintain them in our local cache
      propertyUID: updatedLoan.propertyUID,
      // ownerUID removed - implicit in user-nested path
      startDate: updatedLoan.startDate,
      numberOfMonths: updatedLoan.numberOfMonths,
      simulation: updatedLoan.simulation,
      loanProvider: updatedLoan.loanProvider || { name: '', lenderUID: '' },
      interest: updatedLoan.interest,
      capital: updatedLoan.capital,
      creditCost: updatedLoan.creditCost,
      loanType: updatedLoan.loanType,
      // Building-loan attachment + per-unit split. `?? null` (not undefined —
      // frontend Firestore rejects undefined) so editing a loan back to a
      // single property clears any previous building attachment.
      buildingGroupUID: updatedLoan.buildingGroupUID ?? null,
      allocations: updatedLoan.allocations ?? null,
      // Lifecycle amendments + status. `?? null` clears the back-link on loans
      // that were never created by a refinancing.
      loanEvents: this.serializeEvents(updatedLoan.loanEvents),
      status: updatedLoan.status,
      refinancedFromLoanUID: updatedLoan.refinancedFromLoanUID ?? null,
      loanTable: updatedLoan.loanTable.map(entry => ({
        period: entry.period,
        date: entry.date,
        startingPrincipal: entry.startingPrincipal,
        interest: entry.interest,
        principalRepayment: entry.principalRepayment,
        endingPrincipal: entry.endingPrincipal,
        fees: entry.fees,
        totalCashout: entry.totalCashout
      }))
    };

    // Use addTimestamps helper
    const updateData = this.addTimestamps(loanToUpdate, true);

    return from(updateDoc(loanDocRef, updateData)).pipe(
      tap(() => {
        const index = this.loansData.findIndex(loan => loan.loanUID === loanUID);
        if (index !== -1) {
          this.loansData[index] = updatedLoan;
          this.loans.next([...this.loansData]); // Create a new array to ensure change detection
        } else if (!this.loansData.some(l => l.loanUID === loanUID)) {
          // Fallback: add the loan to the cache if it's not found AND the
          // snapshot listener hasn't already populated it.
          updatedLoan.loanUID = loanUID;
          this.loansData.push(updatedLoan);
          this.loans.next([...this.loansData]);
        }
      }),
      catchError(error => {
        console.error('[LoansService] Update error:', error);
        return throwError(() => new Error(error.message || 'Database error'));
      })
    );
  }

  deleteLoan(loanUID: string): Observable<void> {
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    const loanDocRef = this.getDocument(loanUID);
    return from(deleteDoc(loanDocRef)).pipe(
      tap(() => {
        this.loansData = this.loansData.filter(loan => loan.loanUID !== loanUID);
        this.loans.next(this.loansData);
      }),
      catchError(this.handleError)
    );
  }

  getLoanCount(propertyUID: string): Observable<number> {
    return this.getLoansByProperty(propertyUID).pipe(
      map(loans => loans.length)
    );
  }

  getTotalLoanAmount(propertyUID: string): Observable<number> {
    return this.getLoansByProperty(propertyUID).pipe(
      map(loans => loans.reduce((total, loan) => total + loan.capital.nominalAmount, 0))
    );
  }

  private clearLoans(): void {
    // Tear down the active Firestore listener BEFORE wiping local state.
    // Without this, on logout the listener fires one last permission-denied
    // before unsubscribe propagates — polluting the console and leaking a
    // race during logout/login of a different account.
    this.subsLoans.unsubscribe();
    this.subsLoans = new Subscription();
    this.loansData = [];
    this.loans.next([]);
  }

  private handleError = (error: unknown) => {
    // Silence permission-denied unconditionally in this real-time listener
    // catchError — see BaseCrudService.handleError for the rationale.
    if (isPermissionDeniedError(error)) {
      return EMPTY;
    }
    console.error('An error occurred', error);
    const message = error instanceof Error ? error.message : 'Database error';
    return throwError(() => new Error(message));
  };
}